import tokens from '@skodaflow/web-tokens/src/tokens.json';
import { generateTypography } from '../typography/typography.theme';
import { BreakpointsOptions, Components, Theme, CSSObject } from '@mui/material';
import { fonts } from './fonts';

export const getFlowCssBaselineRoot = (
  scoped: boolean,
  breakpointsOptions: BreakpointsOptions,
  rtl: boolean,
  theme: Theme,
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const baseline: CSSObject = {
    ...(scoped
      ? {
          background: 'unset',
          color: theme.palette.text.primary,
          ...generateTypography('body', breakpointsOptions, rtl),
        }
      : {
          body: {
            background: theme.palette.mode === 'dark' ? theme.palette.onSurface.os900 : theme.palette.common.white,
            color: theme.palette.text.primary,
          },
        }),
    [scoped ? `&, *` : `*`]: {
      // apply scrollbar-* properties only when webkit-scrollbar is not supported
      //  otherwise Chrome since version 121 will not apply style https://stackoverflow.com/a/77931548
      '@supports not selector(::-webkit-scrollbar)': {
        scrollbarWidth: 'thin',
        scrollbarColor: theme.palette.onSurface.os500,
      },
      '::-webkit-scrollbar': {
        border: '2px solid transparent',
        width: '8px',
        height: '8px',
      },
      '::-webkit-scrollbar-thumb': {
        border: '2px solid transparent',
        borderRadius: '100px',
        background: theme.palette.onSurface.os500,
        backgroundClip: 'content-box',
      },
      ':not(.notracks)::-webkit-scrollbar-track': {
        border: '2px solid transparent',
        borderRadius: '100px',
        background: theme.palette.surface.primary,
        backgroundClip: 'content-box',
      },
      '::-webkit-scrollbar-button': {
        display: 'none',
      },
      '::-webkit-scrollbar-corner': {
        backgroundColor: 'transparent',
      },
      // Fix scroll bug on Safari
      // https://github.com/mui/material-ui/issues/5750
      '@media not all and (min-resolution: 0.001dpcm)': {
        '@supports (-webkit-appearance: none)': {
          "[style*='overflow:hidden'], [style*='overflow: hidden']": {
            touchAction: 'none',
          },
        },
      },
    },
    // provide fonts
    fallbacks: !Object.values(fonts).filter(Boolean).length
      ? []
      : [
          {
            '@font-face': {
              fontFamily: `SKODA Next${rtl ? ' R2L' : ''}`,
              src: `
                url('${rtl ? fonts?.rtlFontLightWoff2 : fonts?.fontLightWoff2}') format('woff2'),
                url('${rtl ? fonts?.rtlFontLightWoff : fonts?.fontLightWoff}') format('woff'),
                url('${rtl ? fonts?.rtlFontLightTtf : fonts?.fontLightTtf}') format('truetype')
              `,
              fontStyle: 'normal',
              fontWeight: '300',
            },
          },
          {
            '@font-face': {
              fontFamily: `SKODA Next${rtl ? ' R2L' : ''}`,
              src: `
                url('${rtl ? fonts?.rtlFontRegularWoff2 : fonts?.fontRegularWoff2}') format('woff2'),
                url('${rtl ? fonts?.rtlFontRegularWoff : fonts?.fontRegularWoff}') format('woff'),
                url('${rtl ? fonts?.rtlFontRegularTtf : fonts?.fontRegularTtf}') format('truetype')
              `,
              fontStyle: 'normal',
              fontWeight: '400',
            },
          },
          {
            '@font-face': {
              fontFamily: `SKODA Next${rtl ? ' R2L' : ''}`,
              src: `
                url('${rtl ? fonts?.rtlFontBoldWoff2 : fonts?.fontBoldWoff2}') format('woff2'),
                url('${rtl ? fonts?.rtlFontBoldWoff : fonts?.fontBoldWoff}') format('woff'),
                url('${rtl ? fonts?.rtlFontBoldTtf : fonts?.fontBoldTtf}') format('truetype')
              `,
              fontStyle: 'normal',
              fontWeight: '700',
            },
          },
          {
            '@font-face': {
              fontFamily: `SKODA Next${rtl ? ' R2L' : ''}`,
              src: `
                url('${rtl ? fonts?.rtlFontBlackWoff2 : fonts?.fontBlackWoff2}') format('woff2'),
                url('${rtl ? fonts?.rtlFontBlackWoff : fonts?.fontBlackWoff}') format('woff'),
                url('${rtl ? fonts?.rtlFontBlackTtf : fonts?.fontBlackTtf}') format('truetype')
              `,
              fontStyle: 'normal',
              fontWeight: '900',
            },
          },
        ],
  };

  tokens['flow-typography'].forEach((t) => {
    const el = `${t.name.split('-')[1] === 'list' ? 'typography-' : ''}${t.name.split('-')[1]}`;
    // apply style to plain html tags h1 and classes (.h1)
    //  for ScopedCssBaseline we are applying to tags that outside mui in order
    //  not to introduce css selector with too importance that does not allow overwrites
    //  with direct sx styling
    const key = `${el}${scoped ? ':not([class^="Mui"])' : ''}, .${el}`;
    baseline[key] = generateTypography(el, breakpointsOptions, rtl);
  });

  return baseline;
};

export const getFlowScopedCssBaseline = (
  breakpointsOptions: BreakpointsOptions,
  rtl: boolean,
): Components<Theme>['MuiScopedCssBaseline'] => ({
  styleOverrides: {
    root: ({ theme }) => getFlowCssBaselineRoot(true, breakpointsOptions, rtl, theme),
  },
});

export const getFlowCssBaseline = (
  breakpointsOptions: BreakpointsOptions,
  rtl: boolean,
): Components<Theme>['MuiCssBaseline'] => ({
  styleOverrides: (theme) => getFlowCssBaselineRoot(false, breakpointsOptions, rtl, theme),
});
